import { useState } from 'react';
import { ETIQUETA_DE_SEGUIMIENTO } from '@medchoice/shared';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { irA, volver } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  Avatar, Boton, Cargando, Entrada, EstadoVacio, FalloDeCarga, Insignia, InsigniaEstadoTurno, Kpi, Tarjeta, useTitulo,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { decimal, fechaCorta, pesos } from '../../ui/format';
import { SelloPaciente, nombreDe } from './piezas';

export function Pacientes() {
  const pedido = usePedido(() => consultorio.pacientes(), []);
  const [busqueda, setBusqueda] = useState('');
  const texto = busqueda.trim().toLowerCase();
  const filas = (pedido.datos ?? []).filter((p) => !texto || nombreDe(p).toLowerCase().includes(texto));

  return (
    <SeccionPanel titulo="Pacientes"
      descripcion={pedido.datos ? `${pedido.datos.length} pacientes atendidos o con turno` : undefined}>
      {pedido.estado === 'cargando' && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.datos && (
        <Tarjeta>
          <div className="listado">
            <Entrada aria-label="Buscar paciente" icono="search" placeholder="Buscar por nombre" maxLength={50}
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            {filas.length === 0 ? (
              <EstadoVacio icono="users" titulo="Sin pacientes" cuerpo="Todavía no tenés pacientes que coincidan." />
            ) : (
              <div className="tabla-desplazable">
                <table className="tabla">
                  <thead>
                    <tr><th>Paciente</th><th>Obra social</th><th>Turnos</th><th>Ausencias</th><th>Último</th><th>Próximo</th><th /></tr>
                  </thead>
                  <tbody>
                    {filas.map((p) => (
                      <tr key={p.id_paciente}>
                        <td>
                          <div className="celda-paciente">
                            <Avatar nombre={nombreDe(p)} tamano={32} decorativo />
                            <div><strong>{nombreDe(p)}</strong><SelloPaciente reputacion={p.reputacion} /></div>
                          </div>
                        </td>
                        <td>{p.obra_social ?? 'Particular'}</td>
                        <td className="numero">{p.turnos}</td>
                        <td>{p.ausencias > 0 ? <Insignia tono="ausente" tamano="chico">{p.ausencias}</Insignia> : '—'}</td>
                        <td>{p.ultimo_turno ? fechaCorta(p.ultimo_turno) : '—'}</td>
                        <td>{p.proximo_turno ? <strong className="proximo">{fechaCorta(p.proximo_turno)}</strong> : '—'}</td>
                        <td className="derecha">
                          <Boton tamano="chico" variante="secundario" onClick={() => irA(`/pro/paciente/${p.id_paciente}`)}>Ver ficha</Boton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Tarjeta>
      )}
    </SeccionPanel>
  );
}

export function FichaPaciente({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => consultorio.paciente(id), [id]);
  useTitulo(pedido.datos && nombreDe(pedido.datos));
  const p = pedido.datos;
  const volverAtras = <Boton tamano="chico" variante="secundario" icono="arrow-left" onClick={() => volver('/pro/pacientes')}>Volver</Boton>;

  if (!p) {
    return (
      <SeccionPanel titulo="Ficha del paciente" acciones={volverAtras}>
        {pedido.estado === 'cargando'
          ? <Cargando />
          : <FalloDeCarga mensaje={pedido.error ?? 'No encontramos al paciente'} alReintentar={pedido.recargar} />}
      </SeccionPanel>
    );
  }

  const atendidos = p.historial.filter((t) => t.estado === 'atendido');
  return (
    <SeccionPanel titulo={nombreDe(p)} acciones={volverAtras}
      descripcion={`${p.obra_social ?? 'Particular'}${p.nro_afiliado ? ` · afiliado ${p.nro_afiliado}` : ''} · DNI ${p.dni}`}>
      <div className="pila">
        <div className="kpis">
          <Kpi etiqueta="Turnos con vos" valor={p.historial.length} icono="calendar" />
          <Kpi etiqueta="Atendidos" valor={atendidos.length} icono="check-circle" />
          <Kpi etiqueta="Ausencias" valor={p.ausencias} icono="user-x"
            pista={p.historial.length ? `${Math.round((p.ausencias / p.historial.length) * 100)}% de sus turnos` : undefined} />
          <Kpi etiqueta="Facturado" valor={pesos(atendidos.reduce((a, t) => a + t.precio, 0))} icono="wallet" />
        </div>
        <div className="dos-columnas">
          <Tarjeta>
            <h3 className="titulo-tarjeta">Historial de turnos</h3>
            {p.historial.length === 0 ? <EstadoVacio icono="calendar" titulo="Sin turnos registrados" /> : (
              <div className="listado">
                {p.historial.map((t) => (
                  <button key={t.id_turno} type="button" className="fila-turno" onClick={() => irA(`/pro/turno/${t.id_turno}`)}>
                    <span className="fila-turno__hora">{fechaCorta(t.fecha)}<br />{t.hora_inicio}</span>
                    <span className="fila-turno__datos"><strong>{t.servicio}</strong><span>{pesos(t.precio)}</span></span>
                    <InsigniaEstadoTurno estado={t.estado} />
                  </button>
                ))}
              </div>
            )}
          </Tarjeta>
          <div className="pila">
            <Tarjeta>
              <h3 className="titulo-tarjeta">Contacto</h3>
              <dl className="datos-modal">
                <div><dt>Teléfono</dt><dd>{p.telefono}</dd></div>
                <div><dt>Correo</dt><dd>{p.email}</dd></div>
                <div><dt>Reputación</dt><dd><SelloPaciente reputacion={p.reputacion} /></dd></div>
                {p.reputacion.puntaje !== null && <div><dt>Puntaje promedio</dt><dd>{decimal(p.reputacion.puntaje)} de 5</dd></div>}
              </dl>
            </Tarjeta>
            <Tarjeta>
              <h3 className="titulo-tarjeta">Notas clínicas</h3>
              {p.notas.length === 0
                ? <p className="nota">Todavía no escribiste notas sobre este paciente. Se agregan desde cada turno.</p>
                : (
                  <ul className="notas">
                    {p.notas.map((n) => <li key={n.id_nota}><span>{fechaCorta(n.fecha.slice(0, 10))}</span>{n.texto}</li>)}
                  </ul>
                )}
            </Tarjeta>
            <Tarjeta>
              <h3 className="titulo-tarjeta">Seguimientos</h3>
              {p.seguimientos.length === 0
                ? <p className="nota">No hay controles programados. Se programan desde un turno atendido.</p>
                : (
                  <ul className="notas">
                    {p.seguimientos.map((s) => (
                      <li key={s.id_seguimiento}>
                        <span>{fechaCorta(s.fecha_programada)}/{s.fecha_programada.slice(2, 4)}</span>
                        {ETIQUETA_DE_SEGUIMIENTO[s.tipo]}{s.estado === 'programado' ? '' : ` · ${s.estado}`}
                      </li>
                    ))}
                  </ul>
                )}
            </Tarjeta>
          </div>
        </div>
      </div>
    </SeccionPanel>
  );
}
